import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1] as const;

const nodes = [
  { x: 140, title: ["Main University", "Hospital"],         pill: "REPORTING",         pillW: 90 },
  { x: 305, title: ["Research /", "AI Lab"],                pill: "HOME-GROWN AI",   pillW: 118 },
  { x: 470, title: ["Oncology", "Center"],                  pill: "TUMOR BOARD",       pillW: 104 },
  { x: 635, title: ["Outpatient Imaging", "Center"],        pill: "SCREENING",         pillW: 92 },
  { x: 800, title: ["Emergency /", "Acute"],                pill: "ACUTE STROKE",      pillW: 110 },
];

// Vertical layout (top → bottom):
//   tangled curves (arc up above the icons)
//   icon circle    (y = 175)
//   site name      (y = 215, 2 lines)
//   pill           (y = 278)
//   vertical drops (y = 295 → 440)
//   deepcOS bar    (y = 440 → 560)
const BAR_TOP = 440;
const BAR_H = 120;
const ICON_Y = 175;
const PILL_Y = 263;
const DROP_TOP = 280;

const IntegrationFanOut = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });
  const reduce = useReducedMotion();

  const stroke = "hsl(var(--primary))";

  return (
    <div ref={ref} className="relative w-full">
      <style>{`
        @keyframes ifo-flow {
          from { stroke-dashoffset: 84; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes ifo-flow-up {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: 84; }
        }
        .ifo-pulse { animation: ifo-flow 2.4s linear infinite; }
        .ifo-pulse-up { animation: ifo-flow-up 2.4s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .ifo-pulse, .ifo-pulse-up { animation: none; opacity: 0.35; }
        }
      `}</style>
      <svg
        viewBox="0 0 900 620"
        className="w-full h-auto block"
        role="img"
        aria-label="Five clinical workflows — reporting, home-grown AI, tumor board, screening, and acute stroke — running on top of the deepcOS foundation."
      >
      <defs>
        <linearGradient id="fabricBg" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%"   stopColor={stroke} stopOpacity="0.04" />
          <stop offset="50%"  stopColor={stroke} stopOpacity="0.10" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0.04" />
        </linearGradient>
      </defs>

      {/* Tangled curves — arc UPWARD above the workflow icons */}
      <g stroke={stroke} fill="none" strokeLinecap="round">
        <g strokeOpacity={0.5} strokeWidth={1.3} strokeDasharray="4 5">
          {[
            `M 140,${ICON_Y - 20} C 195,${ICON_Y - 115} 250,${ICON_Y - 115} 305,${ICON_Y - 20}`,
            `M 305,${ICON_Y - 20} C 360,${ICON_Y - 115} 415,${ICON_Y - 115} 470,${ICON_Y - 20}`,
            `M 470,${ICON_Y - 20} C 525,${ICON_Y - 115} 580,${ICON_Y - 115} 635,${ICON_Y - 20}`,
            `M 635,${ICON_Y - 20} C 690,${ICON_Y - 115} 745,${ICON_Y - 115} 800,${ICON_Y - 20}`,
          ].map((d, i) => (
            <motion.path
              key={`arc-${i}`}
              d={d}
              initial={{ pathLength: reduce ? 1 : 0, opacity: 0 }}
              animate={inView ? { pathLength: 1, opacity: 1 } : {}}
              transition={{ duration: 0.9, ease, delay: 0.2 + i * 0.07 }}
            />
          ))}
        </g>
        <g strokeOpacity={0.35} strokeWidth={1.1} strokeDasharray="3 6">
          {[
            `M 140,${ICON_Y - 20} C 305,${ICON_Y - 150} 470,${ICON_Y - 150} 635,${ICON_Y - 20}`,
            `M 305,${ICON_Y - 20} C 470,${ICON_Y - 150} 635,${ICON_Y - 150} 800,${ICON_Y - 20}`,
            `M 140,${ICON_Y - 20} C 360,${ICON_Y - 165} 580,${ICON_Y - 165} 800,${ICON_Y - 20}`,
          ].map((d, i) => (
            <motion.path
              key={`outer-${i}`}
              d={d}
              initial={{ pathLength: reduce ? 1 : 0, opacity: 0 }}
              animate={inView ? { pathLength: 1, opacity: 1 } : {}}
              transition={{ duration: 1.1, ease, delay: 0.35 + i * 0.08 }}
            />
          ))}
        </g>

        {/* Flowing pulses on the curves — randomized direction & timing */}
        <g fill="none" strokeLinecap="round">
          {[
            { d: `M 140,${ICON_Y - 20} C 195,${ICON_Y - 115} 250,${ICON_Y - 115} 305,${ICON_Y - 20}`, up: false, delay: 0.0,  dur: 2.6 },
            { d: `M 305,${ICON_Y - 20} C 360,${ICON_Y - 115} 415,${ICON_Y - 115} 470,${ICON_Y - 20}`, up: true,  delay: 0.9,  dur: 2.2 },
            { d: `M 470,${ICON_Y - 20} C 525,${ICON_Y - 115} 580,${ICON_Y - 115} 635,${ICON_Y - 20}`, up: false, delay: 1.7,  dur: 3.0 },
            { d: `M 635,${ICON_Y - 20} C 690,${ICON_Y - 115} 745,${ICON_Y - 115} 800,${ICON_Y - 20}`, up: true,  delay: 0.4,  dur: 2.4 },
          ].map((c, i) => (
            <path
              key={`arc-pulse-${i}`}
              d={c.d}
              stroke={stroke}
              strokeWidth={2.2}
              strokeDasharray="6 78"
              className={c.up ? "ifo-pulse-up" : "ifo-pulse"}
              style={{ animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s` }}
            />
          ))}
          {[
            { d: `M 140,${ICON_Y - 20} C 305,${ICON_Y - 150} 470,${ICON_Y - 150} 635,${ICON_Y - 20}`, up: true,  delay: 1.2, dur: 3.2 },
            { d: `M 305,${ICON_Y - 20} C 470,${ICON_Y - 150} 635,${ICON_Y - 150} 800,${ICON_Y - 20}`, up: false, delay: 0.3, dur: 2.8 },
            { d: `M 140,${ICON_Y - 20} C 360,${ICON_Y - 165} 580,${ICON_Y - 165} 800,${ICON_Y - 20}`, up: true,  delay: 2.0, dur: 3.6 },
          ].map((c, i) => (
            <path
              key={`outer-pulse-${i}`}
              d={c.d}
              stroke={stroke}
              strokeOpacity={0.85}
              strokeWidth={2}
              strokeDasharray="6 78"
              className={c.up ? "ifo-pulse-up" : "ifo-pulse"}
              style={{ animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s` }}
            />
          ))}
        </g>
      </g>

      {/* Workflow nodes — icon, name, pill stacked above the foundation */}
      {nodes.map((n, i) => (
        <motion.g
          key={`node-${i}`}
          initial={{ opacity: 0, y: reduce ? 0 : -6 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease, delay: 0.6 + i * 0.06 }}
        >
          {/* icon circle */}
          <circle cx={n.x} cy={ICON_Y} r={20} fill="hsl(var(--background))" stroke={stroke} strokeWidth={1.6} />
          <g transform={`translate(${n.x},${ICON_Y})`} stroke={stroke} strokeWidth={1.6} fill="none" strokeLinecap="round" strokeLinejoin="round">
            {i === 0 && (
              <>
                <rect x={-8} y={-7} width={16} height={14} rx={1} />
                <line x1={0} y1={-3} x2={0} y2={3} />
                <line x1={-3} y1={0} x2={3} y2={0} />
                <line x1={-8} y1={7} x2={8} y2={7} />
              </>
            )}
            {i === 1 && (
              <>
                <line x1={-4} y1={-8} x2={4} y2={-8} />
                <path d="M -3,-8 L -3,-2 L -7,6 Q -7,9 -4,9 L 4,9 Q 7,9 7,6 L 3,-2 L 3,-8" />
                <line x1={-5.5} y1={2} x2={5.5} y2={2} />
              </>
            )}
            {i === 2 && (
              <>
                <circle cx={0} cy={0} r={7.5} />
                <circle cx={0} cy={0} r={3.5} />
                <line x1={-10} y1={0} x2={-8} y2={0} />
                <line x1={8} y1={0} x2={10} y2={0} />
                <line x1={0} y1={-10} x2={0} y2={-8} />
                <line x1={0} y1={8} x2={0} y2={10} />
              </>
            )}
            {i === 3 && (
              <>
                <rect x={-9} y={-7} width={18} height={12} rx={1.5} />
                <line x1={-5} y1={9} x2={5} y2={9} />
                <line x1={0} y1={5} x2={0} y2={9} />
                <polyline points="-5,-1 -2,-1 0,-4 2,2 4,-1 5,-1" />
              </>
            )}
            {i === 4 && (
              <path d="M 2,-10 L -5,1 L 0,1 L -2,10 L 6,-2 L 1,-2 L 3,-10 Z" />
            )}
          </g>

          {/* labels (two-line header) */}
          <text x={n.x} y={ICON_Y + 40} textAnchor="middle" fontSize={16} fontWeight={600} fill="hsl(var(--foreground))" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
            <tspan x={n.x} dy={0}>{n.title[0]}</tspan>
            <tspan x={n.x} dy={20}>{n.title[1]}</tspan>
          </text>

          {/* pill */}
          <g transform={`translate(${n.x},${PILL_Y})`}>
            <rect x={(-n.pillW * 1.3) / 2} y={-14} width={n.pillW * 1.3} height={28} rx={14} fill={stroke} fillOpacity={0.1} />
            <text x={0} y={5} textAnchor="middle" fontSize={13} fontWeight={600} fill={stroke} style={{ letterSpacing: "1.2px", fontFamily: "Inter, system-ui, sans-serif" }}>
              {n.pill}
            </text>
          </g>
        </motion.g>
      ))}

      {/* Vertical drops from pills down into the foundation bar */}
      {nodes.map((n, i) => (
        <g key={`drop-${i}`}>
          <motion.line
            x1={n.x} y1={DROP_TOP} x2={n.x} y2={BAR_TOP}
            stroke={stroke} strokeOpacity={0.55} strokeWidth={1.4} fill="none"
            initial={{ pathLength: reduce ? 1 : 0, opacity: 0 }}
            animate={inView ? { pathLength: 1, opacity: 1 } : {}}
            transition={{ duration: 0.7, ease, delay: 0.9 + i * 0.06 }}
          />
          <line
            x1={n.x} y1={DROP_TOP} x2={n.x} y2={BAR_TOP}
            stroke={stroke} strokeWidth={2.4} fill="none" strokeLinecap="round"
            strokeDasharray="6 78"
            className="ifo-pulse"
            style={{ animationDelay: `${i * 0.4}s` }}
          />
          <motion.circle
            cx={n.x} cy={BAR_TOP} r={3.2} fill={stroke}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.3, ease, delay: 1.1 + i * 0.06 }}
          />
        </g>
      ))}

      {/* deepcOS foundation bar (bottom) */}
      <motion.g
        initial={{ opacity: 0, y: reduce ? 0 : 6 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease }}
      >
        <rect x={80} y={BAR_TOP} width={780} height={BAR_H} rx={20} fill="hsl(var(--background))" />
        <rect x={80} y={BAR_TOP} width={780} height={BAR_H} rx={20} fill="url(#fabricBg)" stroke={stroke} strokeOpacity={0.28} strokeWidth={1.2} />
      </motion.g>

      </svg>

      {/* HTML overlay: Apple-style animated text gradient for deepcOS — centered on the foundation bar */}
      <div
        className="pointer-events-none absolute left-0 right-0 flex justify-center"
        style={{ top: `calc(${BAR_TOP + BAR_H / 2} / 620 * 100%)`, transform: "translateY(-50%)" }}
        aria-hidden="true"
      >
        <span
          className="animate-text-gradient bg-clip-text text-transparent font-semibold"
          style={{
            fontSize: "clamp(20px, 3vw, 30px)",
            letterSpacing: "-0.02em",
            backgroundImage:
              "radial-gradient(circle at 100%, hsl(var(--primary) / 0.55), hsl(var(--primary)) 50%, hsl(var(--primary-glow, var(--primary)) / 0.85) 75%, hsl(var(--primary)) 100%)",
            backgroundSize: "200% auto",
          }}
        >
          deepcOS
        </span>
      </div>
    </div>
  );
};

export default IntegrationFanOut;
